import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const FIELD_TYPES = [["text", "Text"], ["number", "Number"], ["dropdown", "Dropdown"], ["yesno", "Yes / No"]];
const inputCls = "w-full rounded-xl border border-[#27272A] bg-[#0A0D10] px-3.5 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#E50914] transition-colors";

// Host custom fields ("driver questions") for a commercial property — answered
// by drivers during booking, validated server-side, snapshotted onto bookings.
export default function PropertyFields({ propertyId, categories }) {
  const [fields, setFields] = useState(null);
  const [name, setName] = useState("");
  const [type, setType] = useState("text");
  const [options, setOptions] = useState("");
  const [required, setRequired] = useState(false);
  const [categoryId, setCategoryId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.propertyFields(propertyId).then(setFields).catch((e) => { toast.error(e.message); setFields([]); });
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [propertyId]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.propertyFieldCreate(propertyId, {
        name, field_type: type, required,
        options: type === "dropdown" ? options.split(",").map((o) => o.trim()).filter(Boolean) : [],
        category_id: categoryId || null,
      });
      setName(""); setOptions(""); setRequired(false); setCategoryId("");
      toast.success("Field added — drivers will answer it during booking.");
      await load();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const toggle = async (f) => {
    try { await api.propertyFieldPatch(propertyId, f.field_id, { active: !f.active }); await load(); }
    catch (e) { toast.error(e.message); }
  };
  const remove = async (f) => {
    try { await api.propertyFieldDelete(propertyId, f.field_id); toast.success("Field removed"); await load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="card-dark p-6" data-testid="property-fields">
      <p className="meta-label">Driver questions (custom fields)</p>
      <p className="text-xs text-zinc-500 mt-1.5 leading-relaxed">
        Drivers answer these during booking. Required answers are enforced by the server and stored on the booking.
      </p>
      <div className="mt-4 space-y-2.5" data-testid="property-fields-list">
        {fields === null ? <div className="h-10 rounded-xl bg-white/[0.03] animate-pulse" /> : fields.length === 0 ? (
          <p className="text-xs text-zinc-600" data-testid="property-fields-empty">No custom questions yet.</p>
        ) : fields.map((f) => (
          <div key={f.field_id} className="flex items-center justify-between gap-3 rounded-xl border border-[#27272A] px-3.5 py-2.5" data-testid={`field-row-${f.field_id}`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{f.name} {f.required && <span className="text-[#FF3B45] text-xs">*</span>}</p>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">
                {f.field_type}{f.category_id ? ` · ${(categories.find((c) => c.category_id === f.category_id) || {}).name || "category"}` : " · all categories"}{!f.active && " · inactive"}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => toggle(f)} className={`text-xs font-semibold ${f.active ? "text-amber-400" : "text-emerald-400"}`} data-testid={`field-toggle-${f.field_id}`}>
                {f.active ? "Deactivate" : "Activate"}
              </button>
              <button onClick={() => remove(f)} aria-label="Delete field" className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors" data-testid={`field-delete-${f.field_id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={create} className="mt-5 pt-5 border-t border-[#1F2328] space-y-3" data-testid="property-field-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <input required minLength={2} maxLength={60} value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="Question (e.g. Parking Type)" className={inputCls} data-testid="field-name" aria-label="Field name" />
          <select value={type} onChange={(e) => setType(e.target.value)} className={inputCls} data-testid="field-type" aria-label="Field type">
            {FIELD_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        {type === "dropdown" && (
          <input required value={options} onChange={(e) => setOptions(e.target.value)}
                 placeholder="Options, comma separated (e.g. Resident, Visitor)" className={inputCls} data-testid="field-options" aria-label="Dropdown options" />
        )}
        <div className="grid sm:grid-cols-2 gap-3 items-center">
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className={inputCls} data-testid="field-category" aria-label="Applies to category">
            <option value="">All categories</option>
            {categories.map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
          </select>
          <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer" data-testid="field-required-row">
            <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} data-testid="field-required" />
            Required — booking cannot be created without an answer
          </label>
        </div>
        <button type="submit" disabled={busy} className="btn-secondary !py-2.5 text-xs disabled:opacity-50" data-testid="field-create-submit">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Add question
        </button>
      </form>
    </div>
  );
}
